<script setup lang="ts">
// Métadonnées de la page économiseur
import { LazyFilterModal } from '#components'
import MoneySaverHelp from '~/components/features/MoneySaverHelp.vue'

const LazyMoneySaverGroceryListModeModal = defineAsyncComponent(() => import('~/components/modals/MoneySaverGroceryListModeModal.vue'))
const LazyMoneySaverFlyersModeModal = defineAsyncComponent(() => import('~/components/modals/MoneySaverFlyersModeModal.vue'))
const LazyMoneySaverSpecificItemsModeModal = defineAsyncComponent(() => import('~/components/modals/MoneySaverSpecificItemsModeModal.vue'))

useHead({
  title: 'L\'économiseur - Trouvez les meilleures recettes au meilleur prix | Glouton',
  meta: [
    {
      name: 'description',
      content: 'Comparez les circulaires, choisissez vos rabais préférés et découvrez des recettes optimisées pour votre budget grâce à l\'économiseur Glouton.',
    },
    {
      name: 'keywords',
      content: 'économies épicerie, recettes économiques, circulaires, rabais épicerie, glouton économiseur, planification repas',
    },
    {
      property: 'og:title',
      content: 'L\'économiseur Glouton | Recettes au meilleur prix',
    },
    {
      property: 'og:description',
      content: 'Activez l\'économiseur Glouton pour repérer les meilleures recettes selon les spéciaux en cours et vos rabais favoris.',
    },
    {
      property: 'og:image',
      content: 'https://glouton.b-cdn.net/site/images/social.jpg',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/economiseur',
  },
})

definePageMeta({
  name: 'MoneySaverPage',
})

const userStore = useUserStore()
const config = useRuntimeConfig()
const route = useRoute()
const overlay = useOverlayWithAd()
const filterStore = useFilterStore()
const localePath = useLocalePath()
const baseStore = useBaseStore()
const eventBus = useEventBusStore()
const cacheStore = useCacheStore()

const page = ref(1)

// your explicit refs
interface Recipe {
  id: number
  title?: string
  thumbnail_url?: string
  slug?: string
  website_name?: string
  [key: string]: any
}

const recipes = ref<Recipe[]>([])
const totalPages = ref(1)
const totalResults = ref(0)

// Mode selection
const selectedMode = ref<string | null>(null)
const selectedFlyerIds = ref<number[]>([])
const selectedFlyerItemIds = ref<number[]>([])
const fridgeCleaner = ref(0)
const includeOtherStoreDeals = ref(0)
const beenStarted = ref(false)
const isLoading = ref(false)
const showHelp = ref(false)
const tipMessages = [
  'Si plusieurs rabais sont disponibles pour le même ingrédient, le plus avantageux est affiché.',
  'Cliquez sur un rabais pour des détails et l\'ajouter à votre liste d\'épicerie.',
]
const currentTip = ref(tipMessages[Math.floor(Math.random() * tipMessages.length)])
const lastFlyersConfig = ref<{ flyerIds: number[], fridgeCleaner: boolean } | null>(null)
const lastGroceryListConfig = ref<{ flyerItemIds: number[], fridgeCleaner: boolean } | null>(null)
const lastSpecificItemsConfig = ref<{ items: any[], includeOtherStoreDeals: boolean, fridgeCleaner: boolean } | null>(null)

const CACHE_KEY = 'MoneySaverV2Page-state'

interface MoneySaverState {
  selectedMode: string | null
  selectedFlyerIds: number[]
  selectedFlyerItemIds: number[]
  fridgeCleaner: number
  includeOtherStoreDeals: number
  lastFlyersConfig: { flyerIds: number[], fridgeCleaner: boolean } | null
  lastGroceryListConfig: { flyerItemIds: number[], fridgeCleaner: boolean } | null
  lastSpecificItemsConfig: { items: any[], includeOtherStoreDeals: boolean, fridgeCleaner: boolean } | null
  beenStarted: boolean
  page: number
  showHelp: boolean
  recipes: Recipe[]
  totalPages: number
  totalResults: number
  currentTip: string
}

let isRestoringState = true

const cachedState = cacheStore.getCache(CACHE_KEY) as MoneySaverState | undefined
if (cachedState) {
  selectedMode.value = cachedState.selectedMode ?? null
  selectedFlyerIds.value = Array.isArray(cachedState.selectedFlyerIds) ? [...cachedState.selectedFlyerIds] : []
  selectedFlyerItemIds.value = Array.isArray(cachedState.selectedFlyerItemIds) ? [...cachedState.selectedFlyerItemIds] : []
  fridgeCleaner.value = cachedState.fridgeCleaner ?? 0
  includeOtherStoreDeals.value = cachedState.includeOtherStoreDeals ?? 0
  lastFlyersConfig.value = cachedState.lastFlyersConfig ? cloneDeep(cachedState.lastFlyersConfig) : null
  lastGroceryListConfig.value = cachedState.lastGroceryListConfig ? cloneDeep(cachedState.lastGroceryListConfig) : null
  lastSpecificItemsConfig.value = cachedState.lastSpecificItemsConfig ? cloneDeep(cachedState.lastSpecificItemsConfig) : null
  beenStarted.value = cachedState.beenStarted ?? false
  page.value = cachedState.page ?? 1
  showHelp.value = cachedState.showHelp ?? false
  recipes.value = Array.isArray(cachedState.recipes) ? cloneDeep(cachedState.recipes) : []
  totalPages.value = cachedState.totalPages ?? 1
  totalResults.value = cachedState.totalResults ?? 0
  if (cachedState.currentTip)
    currentTip.value = cachedState.currentTip
}

watch(() => ({
  selectedMode: selectedMode.value,
  selectedFlyerIds: selectedFlyerIds.value,
  selectedFlyerItemIds: selectedFlyerItemIds.value,
  fridgeCleaner: fridgeCleaner.value,
  includeOtherStoreDeals: includeOtherStoreDeals.value,
  lastFlyersConfig: lastFlyersConfig.value,
  lastGroceryListConfig: lastGroceryListConfig.value,
  lastSpecificItemsConfig: lastSpecificItemsConfig.value,
  beenStarted: beenStarted.value,
  page: page.value,
  showHelp: showHelp.value,
  recipes: recipes.value,
  totalPages: totalPages.value,
  totalResults: totalResults.value,
  currentTip: currentTip.value,
}), (newState) => {
  if (isRestoringState)
    return
  cacheStore.setCache(CACHE_KEY, cloneDeep(newState))
}, { deep: true })

isRestoringState = false

function clearCachedState() {
  cacheStore.clearCache(CACHE_KEY)
}

const tabs = computed(() => {
  return [
    {
      label: 'Toutes les recettes',
      to: localePath({ name: 'RecipesPage' }),
      active: route.name === 'RecipesPage___fr',
      extra: false,
    },
    {
      label: 'L\'économiseur',
      to: localePath({ name: 'ShoppingListPage' }),
      active: route.name === 'MoneySaverPage___fr',
      extra: true,
    },
  ]
})

const selectedModeLabel = computed(() => {
  if (!selectedMode.value)
    return undefined
  switch (selectedMode.value) {
    case 'grocery_list':
      return 'À partir de votre liste d\'épicerie'
    case 'flyers':
      return 'À partir des circulaires'
    case 'specific_items':
      return 'À partir de rabais sélectionnés'
    default:
      return undefined
  }
})

const canEditCurrentMode = computed(() => {
  switch (selectedMode.value) {
    case 'flyers':
      return !!lastFlyersConfig.value
    case 'grocery_list':
      return !!lastGroceryListConfig.value
    case 'specific_items':
      return !!lastSpecificItemsConfig.value
    default:
      return false
  }
})

// Watcher pour recharger les recettes quand la page change (pagination)
watch(page, (newPage, oldPage) => {
  // Seulement recharger si c'est un vrai changement de page (pas la restauration initiale)
  if (beenStarted.value && oldPage !== undefined) {
    fetchRecipes()

    // Scroll vers l'ancre après changement de page
    if (import.meta.client) {
      const el = document.getElementById('MoneySaverPage-anchor')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
})

async function openFilterModal() {
  const modal = overlay.create(LazyFilterModal)
  const modalInstance = modal.open()
  const input = await modalInstance.result
  if (input !== false) {
    page.value = 1
    await fetchRecipes()
  }
}

function cloneDeep<T>(value: T): T {
  if (value === null || value === undefined)
    return value
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value)
    }
    catch (error) {
      console.warn('structuredClone failed, falling back to JSON clone.', error)
    }
  }
  try {
    return JSON.parse(JSON.stringify(value)) as T
  }
  catch (error) {
    console.error('cloneDeep failed, returning original value.', error)
    return value
  }
}

function isEvent(input: unknown): input is Event {
  return typeof input === 'object' && input !== null && 'preventDefault' in input
}

function openGroceryListModal(event: Event): void
function openGroceryListModal(config?: { flyerItemIds?: number[], fridgeCleaner?: boolean }): void
function openGroceryListModal(input?: { flyerItemIds?: number[], fridgeCleaner?: boolean } | Event) {
  const config = isEvent(input) ? undefined : input
  const modal = overlay.create(LazyMoneySaverGroceryListModeModal, {
    props: {
      initialSelectedFlyerItemIds: config?.flyerItemIds ?? [],
      initialFridgeCleaner: config?.fridgeCleaner ?? false,
    },
  })
  modal.open()
}

function openFlyersModeModal(event: Event): void
function openFlyersModeModal(config?: { flyerIds?: number[], fridgeCleaner?: boolean }): void
function openFlyersModeModal(input?: { flyerIds?: number[], fridgeCleaner?: boolean } | Event) {
  const config = isEvent(input) ? undefined : input
  const modal = overlay.create(LazyMoneySaverFlyersModeModal, {
    props: {
      initialFlyerIds: config?.flyerIds ?? [],
      initialFridgeCleaner: config?.fridgeCleaner ?? false,
    },
  })
  modal.open()
}

function openSpecificItemsModal(event: Event): void
function openSpecificItemsModal(config?: { items?: any[], includeOtherStoreDeals?: boolean, fridgeCleaner?: boolean }): void
function openSpecificItemsModal(input?: { items?: any[], includeOtherStoreDeals?: boolean, fridgeCleaner?: boolean } | Event) {
  const config = isEvent(input) ? undefined : input
  const modal = overlay.create(LazyMoneySaverSpecificItemsModeModal, {
    props: {
      initialSelectedItems: config?.items ? cloneDeep(config.items) : [],
      initialIncludeOtherStoreDeals: config?.includeOtherStoreDeals ?? false,
      initialFridgeCleaner: config?.fridgeCleaner ?? false,
    },
  })
  modal.open()
}

async function startMode() {
  beenStarted.value = true
  page.value = 1
  await fetchRecipes()
}

function resetMode() {
  selectedMode.value = null
  selectedFlyerIds.value = []
  selectedFlyerItemIds.value = []
  fridgeCleaner.value = 0
  includeOtherStoreDeals.value = 0
  recipes.value = []
  totalResults.value = 0
  totalPages.value = 1
  page.value = 1
  beenStarted.value = false
  lastFlyersConfig.value = null
  lastGroceryListConfig.value = null
  lastSpecificItemsConfig.value = null
  currentTip.value = tipMessages[Math.floor(Math.random() * tipMessages.length)]
  clearCachedState()
}

function editMode() {
  if (!selectedMode.value)
    return

  if (selectedMode.value === 'flyers' && lastFlyersConfig.value) {
    openFlyersModeModal(lastFlyersConfig.value)
  }
  else if (selectedMode.value === 'grocery_list' && lastGroceryListConfig.value) {
    openGroceryListModal(lastGroceryListConfig.value)
  }
  else if (selectedMode.value === 'specific_items' && lastSpecificItemsConfig.value) {
    openSpecificItemsModal(lastSpecificItemsConfig.value)
  }
}

async function fetchRecipes() {
  isLoading.value = true

  try {
    const response = await $fetch(`${config.public.apiUrl}/v2/money-saver/recipes`, {
      method: 'POST',
      body: {
        page: page.value,
        mode: selectedMode.value,
        filters: filterStore.filters,
        strict: filterStore.strict,
        flyer_ids: selectedFlyerIds.value,
        flyer_item_ids: selectedFlyerItemIds.value,
        fridge_cleaner: fridgeCleaner.value,
        include_other_store_deals: includeOtherStoreDeals.value,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    // Traiter la réponse
    if (response && response.success) {
      recipes.value = response.data.recipes || []
      if (recipes.value.length > 0)
        currentTip.value = tipMessages[Math.floor(Math.random() * tipMessages.length)]
      if (response.data.pagination) {
        totalResults.value = response.data.pagination.total || 0
        totalPages.value = response.data.pagination.last_page || 1
        page.value = response.data.pagination.current_page || 1
      }
      else {
        // Fallback si pas de pagination
        totalResults.value = recipes.value.length
        totalPages.value = 1
      }

      // Track recipe prints on client side when recipes are loaded
      if (import.meta.client && recipes.value.length > 0) {
        baseStore.trackRecipePrints(recipes.value, 'tools').catch(() => {
          // Silent fail - tracking shouldn't block the UI
        })
      }
    }
  }
  catch (error) {
    console.error('Erreur lors du chargement des recettes:', error)
    // Gérer l'erreur (afficher un toast, etc.)
  }
  finally {
    isLoading.value = false
  }
}

// Watcher pour recharger les recettes quand le statut utilisateur change
watch(() => userStore.user, (newUser, oldUser) => {
  // Si l'utilisateur passe d'un objet utilisateur à null/false
  if (!newUser && oldUser) {
    recipes.value = []
    totalResults.value = 0
    totalPages.value = 1
    beenStarted.value = false
    clearCachedState()
  }
}, { immediate: false })

// Watcher pour les événements de l'event bus
watch(() => eventBus.lastEvent, (event) => {
  if (event === 'money-saver:start-grocery-list') {
    const flyerItemIds = (eventBus.lastEventData?.flyer_item_ids || []).map((id: any) => Number(id))
    const fridgeCleanerEnabledFlag = eventBus.lastEventData?.fridge_cleaner === 1
    selectedMode.value = 'grocery_list'
    selectedFlyerItemIds.value = flyerItemIds
    fridgeCleaner.value = fridgeCleanerEnabledFlag ? 1 : 0
    lastGroceryListConfig.value = {
      flyerItemIds,
      fridgeCleaner: fridgeCleanerEnabledFlag,
    }
    startMode()
  }
  if (event === 'money-saver:start-flyers') {
    const flyerIds = (eventBus.lastEventData?.flyer_ids || []).map((id: any) => Number(id))
    const fridgeCleanerEnabledFlag = eventBus.lastEventData?.fridge_cleaner === 1
    selectedMode.value = 'flyers'
    selectedFlyerIds.value = flyerIds
    fridgeCleaner.value = fridgeCleanerEnabledFlag ? 1 : 0
    lastFlyersConfig.value = {
      flyerIds,
      fridgeCleaner: fridgeCleanerEnabledFlag,
    }
    startMode()
  }
  if (event === 'money-saver:start-specific-items') {
    const flyerItemIds = (eventBus.lastEventData?.flyer_item_ids || []).map((id: any) => Number(id))
    const fridgeCleanerEnabledFlag = eventBus.lastEventData?.fridge_cleaner === 1
    const includeOtherDealsFlag = eventBus.lastEventData?.include_other_store_deals === 1
    const selectedItems = eventBus.lastEventData?.selected_items ? cloneDeep(eventBus.lastEventData.selected_items) : []
    selectedMode.value = 'specific_items'
    selectedFlyerItemIds.value = flyerItemIds
    fridgeCleaner.value = fridgeCleanerEnabledFlag ? 1 : 0
    includeOtherStoreDeals.value = includeOtherDealsFlag ? 1 : 0
    lastSpecificItemsConfig.value = {
      items: selectedItems,
      includeOtherStoreDeals: includeOtherDealsFlag,
      fridgeCleaner: fridgeCleanerEnabledFlag,
    }
    startMode()
  }
})
</script>

<template>
  <div id="MoneySaverPage">
    <TopNav
      title="L'économiseur"
      description="Trouvez les recettes les plus avantageuses selon les spéciaux des épiceries."
      access="Un abonnement Extra est requis pour utiliser cet outil."
      :total="totalResults"
      :show-help-button="userStore.user"
      :help-status="showHelp"
      :tabs="tabs"
      :tag="selectedModeLabel"
      class="max-w-7xl mx-auto"
      @help-button-click="showHelp = !showHelp"
    />
    <div v-if="userStore.user && !showHelp" class="relative">
      <div class="relative">
        <UContainer id="MoneySaverPage-anchor" class="pb-10">
          <!-- Mode Selectors -->
          <div v-if="!beenStarted" class="my-6">
            <h2 class="text-2xl font-bold text-poivre-900 mb-6 text-center hidden">
              Choisissez un mode
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Mode 1: Flyers -->
              <button
                class="group relative p-4 md:p-6 text-left border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-pointer bg-sauge-100 flex md:block gap-4"
                @click="openFlyersModeModal"
              >
                <div class="flex">
                  <div class="flex items-center md:mb-4">
                    <div class="p-3 bg-sauge/10 rounded-lg group-hover:bg-sauge/20 transition-colors flex items-center justify-center">
                      <UIcon name="i-gridicons-tag" class="size-9 text-sauge" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-poivre-900 mb-2">
                    À partir des circulaires
                  </h3>
                  <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                    Trouver des recettes qui combinent les rabais les plus avantageux des circulaires de mon choix.
                  </p>
                  <div class="mt-4 flex items-center text-sauge">
                    <span class="text-sm font-medium">Commencer</span>
                    <UIcon name="i-mdi-arrow-right" class="ml-2 text-lg" />
                  </div>
                </div>
              </button>

              <!-- Mode 2: Grocery List -->
              <button
                class="group relative p-4 md:p-6 text-left bg-sorbet-100 border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-pointer flex md:block gap-4"
                @click="openGroceryListModal"
              >
                <div class="flex">
                  <div class="flex items-center md:mb-4">
                    <div class="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors flex items-center justify-center">
                      <UIcon name="i-material-symbols-light-list-alt-rounded" class="size-9 text-primary" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-poivre-900 mb-2">
                    À partir de votre liste d'épicerie
                  </h3>
                  <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                    Trouver des recettes à partir des rabais ajoutés à ma liste d'épicerie.
                  </p>
                  <div class="mt-4 flex items-center text-primary">
                    <span class="text-sm font-medium">Commencer</span>
                    <UIcon name="i-mdi-arrow-right" class="ml-2 text-lg" />
                  </div>
                </div>
              </button>

              <!-- Mode 3: Specific Items -->
              <button
                class="group relative p-4 md:p-6 text-left border-0 border-gray-200 rounded-xl hover:border-primary hover:shadow-lg transition-all cursor-pointer bg-glace-100 flex md:block gap-4"
                @click="openSpecificItemsModal"
              >
                <div class="flex">
                  <div class="flex items-center md:mb-4">
                    <div class="p-3 bg-bleuet/5 rounded-lg group-hover:bg-bleuet/10 transition-colors flex items-center justify-center">
                      <UIcon name="i-mdi-store-search" class="size-9 text-bleuet" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 class="text-lg font-bold text-poivre-900 mb-2">
                    À partir de rabais sélectionnés
                  </h3>
                  <p class="text-base text-poivre-600 leading-relaxed flex-grow">
                    Trouver des recettes à partir d'un ou plusieurs rabais de mon choix.
                  </p>
                  <div class="mt-4 flex items-center text-bleuet">
                    <span class="text-sm font-medium">Commencer</span>
                    <UIcon name="i-mdi-arrow-right" class="ml-2 text-lg" />
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- After mode selected -->
          <div v-else>
            <div class="mt-6 flex flex-wrap items-center gap-2">
              <UButton
                v-if="beenStarted"
                color="error"
                icon="i-mdi-restore"
                @click="resetMode"
              />
              <UButton
                v-if="beenStarted && selectedMode"
                color="secondary"
                icon="i-mdi-pencil"
                :disabled="!canEditCurrentMode"
                @click="editMode"
              />
              <UButton color="secondary" icon="i-mdi-tune" @click="openFilterModal">
                Filtrer <UBadge v-if="filterStore.filters.length > 0" size="sm" variant="soft" color="white" class="">
                  {{ filterStore.filters.length }}
                </UBadge>
              </UButton>
            </div>

            <div v-if="beenStarted && !isLoading && recipes.length" class="mt-4 space-y-4">
              <div class="flex items-center gap-3 rounded-lg border-0 bg-sorbet-200 px-3 p-2 xl:p-3 xl:text-base text-sm text-poivre-600">
                <UIcon name="i-mdi-lightbulb-on-outline" class="text-primary size-5 flex-shrink-0" />
                <span>{{ currentTip }}</span>
              </div>
              <div class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
                <ul class="grid grid-cols-1 gap-6 pb-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                  <RecipeCard
                    v-for="recipe in recipes"
                    :key="recipe.id"
                    :recipe="recipe"
                  />
                </ul>
              </div>
              <Paginator class="flex justify-center mt-4" :total-pages="totalPages" :total="totalResults" :per-page="12" :current-page="page" @pagechanged="(newPage) => page = newPage" />
            </div>

            <div
              v-else-if="beenStarted && !isLoading && !recipes.length"
              class="relative block w-full p-12 text-center border-2 border-dashed rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 mt-6"
            >
              <div class="text-xl font-medium">
                Aucun résultat trouvé.
              </div>
              <div>Modifiez les filtres pour obtenir des résultats.</div>
            </div>

            <div
              v-else-if="isLoading"
              class="relative block w-full py-16 text-center"
            >
              <div class="flex flex-col items-center justify-center space-y-4">
                <UIcon name="i-mdi-loading" class="w-16 h-16 text-primary animate-spin" />
                <div class="flex flex-col items-center space-y-2">
                  <p class="text-lg font-medium text-poivre-700">
                    Recherche en cours...
                  </p>
                  <p class="text-sm text-poivre-500">
                    Nous trouvons les recettes les plus avantageuses pour vous!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </UContainer>
      </div>
    </div>

    <div v-else class="pt-6">
      <MoneySaverHelp />
    </div>
  </div>
</template>
